import type { MetricChartSpec, MetricSeriesSpec } from '@/components/charts/generic';
import { calculateChartRange } from '@/components/charts/generic';
import { graphSettings } from '@/config/settings';
import {
    HEALTH_LINE_TENSION,
    HEALTH_POINT_MARKER,
    healthPercentAxis,
    healthTooltipTitle,
    toPercent,
} from '@/routes/Client/Cage/pages/Health/_utils/healthChartSpec';
import type { Locale } from '@/utils/localeFormatters';

/** Temperature behind the wound series: a pale band at about half the colour's strength. */
const TEMPERATURE_COLOR = graphSettings.graphColors.woundTemperature;
const TEMPERATURE_OPACITY = 0.49;
const TEMPERATURE_FADE = { topOpacity: TEMPERATURE_OPACITY, bottomOpacity: 0.06 };
/** Headroom above the warmest day, in percent of the range. */
const TEMPERATURE_PADDING_PERCENT = 2;

/** One wound series: a share (0–1) per day, `null` where the day has no value. */
export interface WoundGraphSeries {
    key: string;
    label: string;
    color: string;
    values: (number | null | undefined)[];
}

export interface WoundGraphSpecInput {
    /** The x keys, one per day, index-aligned with every `values` and `temperature`. */
    days: string[];
    series: WoundGraphSeries[];
    /** Degrees per day, `null` or `undefined` on a day without a reading. */
    temperature: (number | null | undefined)[];
    labels: { axis: string; temperature: string };
    locale: Locale;
    /** Footer lines for the hovered day and the series on screen; see `MetricChartSpec`. */
    tooltipFooterLines?: MetricChartSpec<'line'>['tooltipFooterLines'];
}

function hasValue(value: number | null | undefined): value is number {
    return value != null && !Number.isNaN(value);
}

/**
 * A wound graph: each wound series in percent on the left, the sea temperature as a band on
 * the right. Temperature is drawn only on days a wound series has a value, so a day without
 * wound data stays blank instead of showing temperature alone. The right axis follows the
 * left grid lines and goes when temperature is hidden; both axes fit what is on screen.
 */
export function buildWoundGraphSpec(input: WoundGraphSpecInput): MetricChartSpec<'line'> {
    const { days, series, temperature, labels, locale, tooltipFooterLines } = input;

    const woundSeries: MetricSeriesSpec[] = series.map((entry) => ({
        key: entry.key,
        label: entry.label,
        color: entry.color,
        tension: HEALTH_LINE_TENSION,
        marker: HEALTH_POINT_MARKER,
        data: days.map((day, index) => ({ day, value: toPercent(entry.values[index]) })),
    }));

    const temperaturePoints = days.map((day, index) => {
        const hasWound = series.some((entry) => hasValue(entry.values[index]));
        const degrees = temperature[index];
        return { day, value: hasWound && hasValue(degrees) ? degrees : null };
    });
    const hasTemperature = temperaturePoints.some((point) => point.value !== null);

    const temperatureSeries: MetricSeriesSpec = {
        key: 'temperature',
        label: labels.temperature,
        color: TEMPERATURE_COLOR,
        opacity: TEMPERATURE_OPACITY,
        axis: 'right',
        fill: true,
        fillGradient: TEMPERATURE_FADE,
        hideLine: true,
        tension: HEALTH_LINE_TENSION,
        marker: { radius: 0, hoverRadius: 6, fill: 'series', borderWidth: 2, filledOnHover: true },
        data: temperaturePoints,
    };

    return {
        kind: 'line',
        days,
        series: hasTemperature ? [...woundSeries, temperatureSeries] : woundSeries,
        left: healthPercentAxis(labels.axis),
        right: {
            label: labels.temperature,
            unit: '°C',
            fitRange: (values) => calculateChartRange(values, TEMPERATURE_PADDING_PERCENT, true),
            alignTicksToLeft: true,
            hideWhenUnused: true,
        },
        locale,
        tooltipTitle: healthTooltipTitle(locale),
        tooltipSkipEmpty: true,
        ...(tooltipFooterLines ? { tooltipFooterLines } : {}),
    };
}
